import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Brand";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get(`/auth/reset-token/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo({ valid: false })); }, [token]);
  const invite = info?.purpose === "invite";

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="reset-password-page">
      <div className="w-full max-w-sm animate-fade-up">
        <LogoMark className="w-10 h-10 mb-4" />
        <h1 className="font-display text-3xl font-bold tracking-tight mb-1">{invite ? "Welcome — set your password" : "Choose a new password"}</h1>
        <p className="text-muted-foreground mb-8 text-sm" data-testid="reset-intro">
          {invite ? `Your account (${info.email}) is ready with complimentary access. Choose a password to get started.` : "At least 8 characters. This link works once, for one hour."}
        </p>
        {info && !info.valid && <p className="text-sm text-destructive mb-4" data-testid="reset-invalid">This link is invalid or has expired.</p>}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="password">New password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="mt-1.5" data-testid="reset-password-input" />
          </div>
          <div>
            <Label htmlFor="confirm">Confirm password</Label>
            <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="mt-1.5" data-testid="reset-confirm-input" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
          {invite && <p className="text-xs text-muted-foreground" data-testid="reset-consent">By setting a password you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>}
          <Button type="submit" className="w-full" disabled={loading || (info && !info.valid)} data-testid="reset-submit-button">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : invite ? "Set password & sign in" : "Update password"}</Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6 text-center">Link expired? <Link to="/forgot-password" className="text-primary font-medium hover:underline" data-testid="reset-request-new">Request a new one</Link></p>
      </div>
    </div>
  );
}
